/**
 * useProviderLogin Hook
 *
 * Shared, UI-free provider sign-in flow extracted from `LoginPage`. Both the
 * `/login` screen and the `/delete-account` page need to run the *exact same*
 * full Firebase sign-in + account-setup flow, so the business logic lives here
 * once instead of being duplicated.
 *
 * Mirrors Flutter `lib/presentation/screens/login/login_screen.dart:_login`
 * (lines 79-208):
 *   Step 1: Firebase sign-in (Google/Apple popup, or — DEV-only, web-only — the
 *           `qa-tester` email/password form) → FirebaseLoginResult
 *   Step 2: saveUser(firebaseUser) → SaveUserResult (removing / suspended)
 *   Step 3: Set Firebase user ID in Redux (auth state)
 *   Step 4: Initialize SecureStorage (encryption setup)
 *   Step 5: Store full user to SecureStorage (source of truth)
 *   Step 6: Trigger sync with full user object (blocked users, etc.)
 *   Step 7: Set full user + premium packages in Redux (sync cache)
 *   Step 8: Clear initializing flag → allow navigation to MainScreen
 *
 * The ordered `removing → !success → suspended` branches from Flutter are
 * preserved exactly.
 *
 * Layer ownership: this hook owns state + Redux `dispatch` (allowed in hooks)
 * but NEVER fires toasts / sheets / dialogs. Every user-visible reaction is
 * delegated to a caller-provided handler so the consuming page keeps ownership
 * of its UI (toast vs sheet vs dialog). The handlers are stashed in a ref so
 * the exposed callbacks stay referentially stable across renders even though
 * callers pass a fresh inline handlers object each render.
 *
 * One deliberate exception to the listener-owns-the-slice rule in
 * `.claude/context/state-slices.md`: steps 7 / 7b dispatch `setUser` and
 * `setUserPremiumPackages` directly instead of leaving both to
 * `AuthUserListener` / `PremiumPackagesListener`. This hook is the sign-in half
 * of the app bootstrap, and the startup half — `AppInitializer`, the site
 * `state-slices.md` already sanctions — makes the same two dispatches for the
 * same reason (`components/app/AppInitializer.tsx:73`, `:103`). The block
 * comment at step 7 names the guarantee each dispatch provides and the listener
 * it front-runs. The exception is local to this bootstrap: from step 8 onward
 * both slices are the listeners' again.
 *
 * The messages passed to `onError` come from `l10n.auth.*` (read through a ref
 * for the same stability reason), so every consumer — `LoginPage` and
 * `DeleteAccountPage` alike — renders localized copy without owning the strings.
 * The exception is the step-1 sign-in failure (popup or email), which forwards
 * `authService`'s own `loginResult.error` verbatim (those literals are a known,
 * deferred domain-layer l10n gap; Flutter hardcodes English there too).
 */

import { useCallback, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import type { User as FirebaseUser } from 'firebase/auth';
import type { AppDispatch } from '@/presentation/store';
import {
  setFirebaseUserId,
  setInitializing,
  setUser,
} from '@/presentation/store/slices/auth/authSlice';
import { setUserPremiumPackages } from '@/presentation/store/slices/premiumPackages/premiumPackagesSlice';
import { signInWithGoogleUseCase } from '@/domain/usecases/login/signInWithGoogleUseCase';
import { signInWithAppleUseCase } from '@/domain/usecases/login/signInWithAppleUseCase';
import { signInWithEmailUseCase } from '@/domain/usecases/login/signInWithEmailUseCase';
import { saveUserUseCase } from '@/domain/usecases/login/saveUserUseCase';
import { initializeSecureStorageUseCase } from '@/domain/usecases/secureStorage/initializeSecureStorageUseCase';
import { storeCurrentUserToLocalStorageUseCase } from '@/domain/usecases/user/storeCurrentUserToLocalStorageUseCase';
import { SyncSecureStorageUseCase } from '@/domain/usecases/sync/syncSecureStorageUseCase';
import { GetUserPremiumPackagesUseCase } from '@/domain/usecases/premiumPackage/getUserPremiumPackagesUseCase';
import { appLogger } from '@/domain/services/analytics/appLogger';
import { useLocale } from '@/presentation/contexts/LocaleContext';

/**
 * Caller-provided reactions to each login outcome. All optional — a consumer
 * wires only the outcomes it cares about. Plain callbacks (not a status union)
 * so each page decides its own UI (toast / sheet / dialog).
 */
export interface ProviderLoginHandlers {
  /** `saveUser` reported the account is being removed (removing = true). */
  onRemoving?: () => void;
  /** The saved user is suspended. */
  onSuspended?: () => void;
  /** A recoverable error occurred; `message` is the user-facing text. */
  onError?: (message: string) => void;
  /** Full success — the user is signed in and app state is populated. */
  onSuccess?: () => void;
}

export interface UseProviderLoginResult {
  /** True while a sign-in / setup flow is in progress (drives loading UI). */
  isLoggingIn: boolean;
  loginWithGoogle: () => Promise<void>;
  loginWithApple: () => Promise<void>;
  /**
   * Step 1 (Firebase email/password) + steps 2-8. **DEV-only**: this is the
   * entry point the `qa-tester` agent signs in through on every branch's QA run
   * — it cannot drive the Google/Apple OAuth popup. `LoginPage` renders the form
   * that calls it behind `import.meta.env.DEV`, so it never ships to production.
   */
  loginWithEmail: (email: string, password: string) => Promise<void>;
}

export const useProviderLogin = (
  handlers?: ProviderLoginHandlers
): UseProviderLoginResult => {
  const dispatch = useDispatch<AppDispatch>();
  const { l10n } = useLocale();
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  // Latest handlers in a ref so the exposed callbacks don't depend on the
  // (fresh-each-render) handlers object and stay referentially stable.
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  // Same ref treatment for the translations: the error copy handed to
  // `onError` must come from the CURRENT locale, but `l10n` is a fresh value on
  // every language change while the returned callbacks (`loginWithGoogle` /
  // `loginWithApple` / `loginWithEmail`) are documented as referentially
  // stable — which requires the internal `completeLogin` they each depend on to
  // be stable too. Reading through the ref keeps the dependency arrays free of
  // a per-render value.
  const l10nRef = useRef(l10n);
  l10nRef.current = l10n;

  // Re-entrancy guard mirrored into a ref (Flutter `_isLoggingIn` guard,
  // login_screen.dart:80-82) so the login callbacks need no state in their
  // dependency arrays.
  const isLoggingInRef = useRef(false);

  // Steps 2-8. Mirrors `login_screen.dart:_login` lines 157-208. Owns the
  // `isLoggingIn` overlay lifecycle for whichever entry point invoked it, so
  // the ordered branches below stay verbatim in logic.
  //
  // Internal to this hook: every entry point below runs it after its own
  // step 1, and it is deliberately NOT part of the returned API, so a page
  // cannot reach in and run a step-1 sign-in of its own (that orchestration
  // belongs here). A new entry point that owns a different step 1 belongs
  // beside `loginWithProvider` / `loginWithEmail`, not in a caller.
  const completeLogin = useCallback(
    async (firebaseUser: FirebaseUser) => {
      const activeHandlers = handlersRef.current;
      setIsLoggingIn(true);
      try {
        // Step 2: Create/update the user record. Mirrors lines 157-200.
        const saveResult = await saveUserUseCase.execute(firebaseUser);

        if (saveResult.removing) {
          activeHandlers?.onRemoving?.();
          dispatch(setInitializing(false));
          return;
        }

        if (!saveResult.success || !saveResult.user) {
          activeHandlers?.onError?.(l10nRef.current.auth.accountSetupError);
          dispatch(setInitializing(false));
          return;
        }

        const userEntity = saveResult.user;

        // TODO: @claude add a follow up task for this: write the user to SecureStorage before the suspended early-return (Flutter saves the local user first) — Flutter lib/presentation/screens/login/login_screen.dart:191-200
        // Flutter: `saveLocalUser(user)` (:193) runs BEFORE `if (user.suspended)` (:195),
        // so a suspended account is still cached locally. The web cannot write before
        // `initializeSecureStorageUseCase` (below) has run with the firebase id, so this
        // early return happens first. Domain-layer parity pass, deliberately out of
        // scope for the presentation-only login polish branch.
        if (userEntity.suspended) {
          activeHandlers?.onSuspended?.();
          dispatch(setInitializing(false));
          return;
        }

        try {
          // Step 3: Update global state with Firebase user ID. This triggers
          // RootScreen's effect but isInitializing=true blocks navigation.
          dispatch(setFirebaseUserId(userEntity.firebaseId));

          // Step 4: Initialize SecureStorage (encryption setup).
          await initializeSecureStorageUseCase.execute(userEntity.firebaseId);

          // Step 5: Store UserEntity to SecureStorage (source of truth).
          await storeCurrentUserToLocalStorageUseCase.execute(userEntity);

          // Step 6: Trigger sync with full user entity (blocked users, etc.).
          const syncSecureStorageUseCase = new SyncSecureStorageUseCase();
          await syncSecureStorageUseCase.execute(userEntity);

          // Steps 7 / 7b dispatch two listener-owned setters directly. That is
          // the documented exception to `.claude/context/state-slices.md`
          // ("never dispatch `setUser` / `setUserPremiumPackages` from feature
          // code"), taken here for the same reason `AppInitializer` takes it on
          // the startup path (AppInitializer.tsx:73, :103): these two writes
          // establish the Redux baseline that the FIRST protected render reads,
          // and that render is unblocked synchronously by step 8's
          // `setInitializing(false)` (`ProtectedRoute` gates on it —
          // routing/AppRoutes.tsx:57). Specifically:
          //
          //   setUser — `AuthUserListener` does mirror the step-5 SecureStorage
          //   write, but a React hop later (eventBus emit → hook setState →
          //   re-render → effect → dispatch), so it is not guaranteed to have
          //   landed in the store state the first protected render sees. The
          //   dispatch below makes `state.auth.user` non-null before step 8.
          //
          //   setUserPremiumPackages — the listener cannot cover login at all.
          //   It only reacts to writes under `USER_PREMIUM_PACKAGES + productId`,
          //   and `SyncSecureStorageUseCase.syncUserPremiumPackages` writes one
          //   key per *returned* package (syncSecureStorageUseCase.ts:528-544):
          //   a user with no active package produces zero writes, zero events,
          //   and `premiumPackages.isLoaded` would stay false for the whole
          //   session. The sync is also fire-and-forget (only WALLET waits —
          //   syncEntityType.ts:70-73), so step 6 returns before any package
          //   write lands; the listener still corrects the list once it does.
          //
          // Everything after this point flows storage → listener → Redux as
          // usual; do not add further direct slice writes to this hook.

          // Step 7: Populate Redux cache with user entity.
          dispatch(setUser(userEntity));

          // Step 7b: Load user premium packages into Redux for sync access.
          const getUserPremiumPackagesUseCase = new GetUserPremiumPackagesUseCase();
          const userPremiumPackages = await getUserPremiumPackagesUseCase.execute();
          dispatch(setUserPremiumPackages(userPremiumPackages));

          // Step 8: Clear initializing flag — allow navigation to MainScreen.
          dispatch(setInitializing(false));
          activeHandlers?.onSuccess?.();
        } catch (error) {
          appLogger.error('Failed to initialize app', 'useProviderLogin.completeLogin', {
            error: error as Error,
          });
          dispatch(setInitializing(false));
          activeHandlers?.onError?.(l10nRef.current.auth.appInitError);
        }
      } finally {
        setIsLoggingIn(false);
      }
    },
    [dispatch]
  );

  // Step 1 (popup) + steps 2-8. Mirrors `login_screen.dart:_login` lines
  // 79-208. `signInWith*UseCase` is Firebase-only; `completeLogin` owns the
  // rest.
  const loginWithProvider = useCallback(
    async (isApple: boolean) => {
      if (isLoggingInRef.current) return;

      const activeHandlers = handlersRef.current;
      isLoggingInRef.current = true;
      setIsLoggingIn(true);

      // Arm the isInitializing gate BEFORE the popup so app-level listeners
      // (ExclusiveContentListener, UserDeviceListener) skip when Firebase's
      // onAuthStateChanged fires inside the popup and dispatches
      // setFirebaseUserId — otherwise they throw "Storage not initialized".
      dispatch(setInitializing(true));

      try {
        // Step 1: Firebase popup sign-in (lines 97-148).
        const loginResult = isApple
          ? await signInWithAppleUseCase.execute()
          : await signInWithGoogleUseCase.execute();

        if (loginResult.error || !loginResult.firebaseUser) {
          if (loginResult.error) activeHandlers?.onError?.(loginResult.error);
          dispatch(setInitializing(false));
          return;
        }

        // Steps 2-8: shared post-Firebase flow.
        await completeLogin(loginResult.firebaseUser);
      } catch (error) {
        appLogger.error('Login error', 'useProviderLogin.loginWithProvider', {
          error: error as Error,
        });
        // Flutter's single `_login` catch-all shows the account-setup message
        // (`login_screen.dart:221-231`, literal at `:228`).
        activeHandlers?.onError?.(l10nRef.current.auth.accountSetupError);
        dispatch(setInitializing(false));
      } finally {
        isLoggingInRef.current = false;
        setIsLoggingIn(false);
      }
    },
    [dispatch, completeLogin]
  );

  const loginWithGoogle = useCallback(
    () => loginWithProvider(false),
    [loginWithProvider]
  );
  const loginWithApple = useCallback(
    () => loginWithProvider(true),
    [loginWithProvider]
  );

  // DEV-ONLY step 1 (email/password) + steps 2-8. Same shape as
  // `loginWithProvider` above — only the step-1 use case differs. It exists
  // because the `qa-tester` agent cannot drive the Google/Apple OAuth popup;
  // `LoginPage` gates the form that calls it behind `import.meta.env.DEV`.
  const loginWithEmail = useCallback(
    async (email: string, password: string) => {
      if (isLoggingInRef.current) return;

      const activeHandlers = handlersRef.current;
      isLoggingInRef.current = true;
      setIsLoggingIn(true);

      // Arm the isInitializing gate BEFORE sign-in so app-level listeners
      // (ExclusiveContentListener, UserDeviceListener) skip when Firebase's
      // onAuthStateChanged fires and dispatches setFirebaseUserId — otherwise
      // they throw "Storage not initialized".
      dispatch(setInitializing(true));

      try {
        // Step 1: Firebase email/password sign-in.
        const loginResult = await signInWithEmailUseCase.execute(email, password);

        if (loginResult.error || !loginResult.firebaseUser) {
          if (loginResult.error) activeHandlers?.onError?.(loginResult.error);
          dispatch(setInitializing(false));
          return;
        }

        // Steps 2-8: shared post-Firebase flow.
        await completeLogin(loginResult.firebaseUser);
      } catch (error) {
        appLogger.error('Email login error', 'useProviderLogin.loginWithEmail', {
          error: error as Error,
        });
        // Same catch-all copy as `loginWithProvider` above — both arms fail
        // through the identical `completeLogin` tail, so the user-visible
        // message must not depend on which button started the flow.
        activeHandlers?.onError?.(l10nRef.current.auth.accountSetupError);
        dispatch(setInitializing(false));
      } finally {
        isLoggingInRef.current = false;
        setIsLoggingIn(false);
      }
    },
    [dispatch, completeLogin]
  );

  return { isLoggingIn, loginWithGoogle, loginWithApple, loginWithEmail };
};
